"use client";
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const StatsSection = () => {
  const [counters, setCounters] = useState([0, 0, 0, 0]);
  
  useEffect(() => {
    const targetValues = [17, 16, 3, 10];
    const duration = 2000; // ms
    const interval = 10; // ms
    const steps = duration / interval;
    
    const countersInterval = setInterval(() => {
      setCounters(prev => prev.map((val, idx) => {
        const target = targetValues[idx];
        const increment = target / steps;
        return val < target ? val + increment : target;
      }));
    }, interval);
    
    return () => clearInterval(countersInterval);
  }, []);

  const stats = [
    { label: "Projects Delivered", suffix: "+", subtitle: "With 100% Success Rate" },
    { label: "Happy Clients", suffix: "+", subtitle: "Who Keep Coming Back" },
    { label: "Years of Excellence", suffix: "+", subtitle: "In Digital Innovation" },
    { label: "Expert Team", suffix: "", subtitle: "Dedicated to Your Success" }
  ];

  return (
<section style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem', paddingLeft: '1rem', paddingRight: '1rem' }} className="sm:py-16 md:py-20 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }} className="text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: '1rem' }}
            className="text-4xl md:text-5xl font-bold text-gray-900"
          >
            <span style={{ color: '#182cd4' }}>Proven Results</span> That Speak for Themselves
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-xl text-gray-600"
          >
            Every number tells a story of success, innovation, and unwavering commitment to excellence
          </motion.p>
        </div>
        
        <div style={{ padding: '1rem' }} className="grid grid-cols-1 fle md:grid-cols-2 lg:grid-cols-4 gap-6 md:p-0">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className="group"
            >
              <div style={{ padding: '2rem' }} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 h-full border border-gray-100 group-hover:border-blue-100">
                <div style={{ marginBottom: '1rem' }} className="flex justify-center">
                  <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors duration-300">
                      <div className="text-blue-600 group-hover:text-blue-800 transition-colors duration-300">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          {index === 0 && (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          )}
                          {index === 1 && (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          )}
                          {index === 2 && (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          )}
                          {index === 3 && (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          )}
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-center">
                  <motion.div 
                    style={{ marginBottom: '0.5rem' }}
                    className="text-4xl md:text-5xl font-bold text-gray-900"
                  >
                    {Math.round(counters[index])}{stat.suffix}
                  </motion.div>
                  <div className="text-lg text-gray-600 font-semibold">{stat.label}</div>
                  <div style={{ marginTop: '0.25rem' }} className="text-sm text-gray-500">{stat.subtitle}</div>
                </div>
                
                <div style={{ marginTop: '1.5rem' }} className="flex justify-center">
                  <div className="h-1 w-16 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full group-hover:w-20 transition-all duration-300"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          style={{ marginTop: '4rem' }}
          className="text-center"
        >
          <div style={{ paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingTop: '0.75rem', paddingBottom: '0.75rem' }} className=" lg:inline-flex hidden items-center gap-4 bg-white rounded-full shadow-sm">
            <div className="flex space-x-1">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse"></div>
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse delay-75"></div>
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse delay-150"></div>
            </div>
            <span className="text-gray-700">📊 Live metrics updated in real-time</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default StatsSection;